import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const extractPayloadData = (payload) => payload?.data ?? payload;

// Tüm aktif testleri getir
export const getAllTests = createAsyncThunk(
  "tests/getAllTests",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/tests`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "There was an error fetching the tests.",
      );
    }
  },
);

// ID ile belirli bir testi getir (sorularıyla birlikte)
export const getTestById = createAsyncThunk(
  "tests/getTestById",
  async (testId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/tests/${testId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Test information not found",
      );
    }
  },
);

// Yeni test oluştur (Sadece ADMIN)
export const createTest = createAsyncThunk(
  "tests/createTest",
  async (testData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");

      if (!token) {
        return rejectWithValue("Session not found");
      }

      if (userType !== "admin") {
        return rejectWithValue("You must be an admin to create tests");
      }

      const response = await axios.post(`${API_URL}/tests`, testData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      console.error(
        "Create test error:",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Test creation failed",
      );
    }
  },
);

// Teste soru ekle (Sadece ADMIN)
export const addQuestionToTest = createAsyncThunk(
  "tests/addQuestionToTest",
  async ({ testId, questionData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");

      if (!token) {
        return rejectWithValue("Session not found");
      }

      if (userType !== "admin") {
        return rejectWithValue("You must be an admin to add questions");
      }

      const response = await axios.post(
        `${API_URL}/tests/${testId}/questions`,
        questionData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      const errData = error.response?.data;
      console.error("Add question error:", JSON.stringify(errData) || error.message);
      const errMsg =
        errData?.message ||
        errData?.error ||
        (typeof errData === "string" ? errData : null) ||
        (errData?.errors ? Object.values(errData.errors).map((e) => e.message).join(", ") : null) ||
        error.message ||
        "Question addition failed";
      return rejectWithValue(errMsg);
    }
  },
);

// Test cevabı gönder
export const submitTestResponse = createAsyncThunk(
  "tests/submitTestResponse",
  async (responseData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }

      const response = await axios.post(
        `${API_URL}/tests/responses`,
        responseData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Test answer sending failed",
      );
    }
  },
);

// Kullanıcının test sonuçlarını getir
export const getUserTestResults = createAsyncThunk(
  "tests/getUserTestResults",
  async (userId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }

      const response = await axios.get(
        `${API_URL}/tests/users/${userId}/test-results`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Test sonuçları getirilemedi.",
      );
    }
  },
);

// Soru silme
export const deleteQuestion = createAsyncThunk(
  "tests/deleteQuestion",
  async ({ testId, questionId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");

      if (!token) {
        return rejectWithValue("Oturum bulunamadı");
      }

      if (userType !== "admin") {
        return rejectWithValue("You must be an admin to delete questions");
      }

      const response = await axios.delete(
        `${API_URL}/tests/${testId}/questions/${questionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return { questionId, ...response.data };
    } catch (error) {
      console.error(
        "Delete question error:",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Soru silme işlemi başarısız oldu.",
      );
    }
  },
);

// Test silme
export const deleteTest = createAsyncThunk(
  "tests/deleteTest",
  async (testId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");

      if (!token) {
        return rejectWithValue("Oturum bulunamadı");
      }

      if (userType !== "admin") {
        return rejectWithValue("You must be an admin to delete tests");
      }

      const response = await axios.delete(`${API_URL}/tests/${testId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return { testId, ...response.data };
    } catch (error) {
      console.error(
        "Delete test error:",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Test silme işlemi başarısız oldu.",
      );
    }
  },
);

// Soru düzenleme
export const updateQuestion = createAsyncThunk(
  "tests/updateQuestion",
  async ({ testId, questionId, questionData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");

      if (!token) {
        return rejectWithValue("Oturum bulunamadı");
      }

      if (userType !== "admin") {
        return rejectWithValue("You must be an admin to update questions");
      }

      const response = await axios.put(
        `${API_URL}/tests/${testId}/questions/${questionId}`,
        questionData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error(
        "Update question error:",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Soru güncelleme işlemi başarısız oldu.",
      );
    }
  },
);

// Test düzenleme
export const updateTest = createAsyncThunk(
  "tests/updateTest",
  async ({ testId, testData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");

      if (!token) {
        return rejectWithValue("Oturum bulunamadı");
      }

      if (userType !== "admin") {
        return rejectWithValue("You must be an admin to update tests");
      }

      const response = await axios.put(`${API_URL}/tests/${testId}`, testData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      console.error(
        "Update test error:",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Test güncelleme işlemi başarısız oldu.",
      );
    }
  },
);

const testSlice = createSlice({
  name: "tests",
  initialState: {
    tests: [],
    currentTest: null,
    currentTestQuestions: [],
    userTestResults: [],
    loading: false,
    error: null,
    createTestSuccess: false,
    addQuestionSuccess: false,
    submitResponseSuccess: false,
    deleteQuestionSuccess: false,
    updateQuestionSuccess: false,
    updateTestSuccess: false,
    deleteTestSuccess: false,
    testResultsLoading: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentTest: (state) => {
      state.currentTest = null;
      state.currentTestQuestions = [];
    },
    resetTestStates: (state) => {
      state.createTestSuccess = false;
      state.addQuestionSuccess = false;
      state.submitResponseSuccess = false;
      state.deleteQuestionSuccess = false;
      state.updateQuestionSuccess = false;
      state.updateTestSuccess = false;
      state.deleteTestSuccess = false;
      state.error = null;
    },
    setCreateTestSuccess: (state, action) => {
      state.createTestSuccess = action.payload;
    },
    setAddQuestionSuccess: (state, action) => {
      state.addQuestionSuccess = action.payload;
    },
    setSubmitResponseSuccess: (state, action) => {
      state.submitResponseSuccess = action.payload;
    },
    clearUserTestResults: (state) => {
      state.userTestResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Tüm Testleri Getirme
      .addCase(getAllTests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTests.fulfilled, (state, action) => {
        state.loading = false;
        const payloadData = extractPayloadData(action.payload);
        state.tests = Array.isArray(payloadData)
          ? payloadData
          : payloadData?.tests || [];
      })
      .addCase(getAllTests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ID ile Test Getirme
      .addCase(getTestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTestById.fulfilled, (state, action) => {
        state.loading = false;
        const payloadData = extractPayloadData(action.payload);
        state.currentTest = payloadData?.test || payloadData || null;
        const incoming = payloadData?.questions || payloadData?.test?.questions || [];
        // Ensure unique questions by _id and sort by questionNumber
        const seen = new Set();
        state.currentTestQuestions = (Array.isArray(incoming) ? incoming : [])
          .filter((q) => {
            if (!q?._id) return false;
            if (seen.has(q._id)) return false;
            seen.add(q._id);
            return true;
          })
          .sort((a, b) => (a.questionNumber || 0) - (b.questionNumber || 0));
      })
      .addCase(getTestById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentTest = null;
        state.currentTestQuestions = [];
      })

      // Test Oluşturma
      .addCase(createTest.pending, (state) => {
        state.loading = true;
        state.createTestSuccess = false;
        state.error = null;
      })
      .addCase(createTest.fulfilled, (state, action) => {
        state.loading = false;
        state.createTestSuccess = true;
        // Yeni testi tests array'ine ekle
        const createdTest = extractPayloadData(action.payload);
        if (createdTest?._id) {
          state.tests.push(createdTest);
        }
      })
      .addCase(createTest.rejected, (state, action) => {
        state.loading = false;
        state.createTestSuccess = false;
        state.error = action.payload;
      })

      // Soru Ekleme
      .addCase(addQuestionToTest.pending, (state) => {
        state.loading = true;
        state.addQuestionSuccess = false;
        state.error = null;
      })
      .addCase(addQuestionToTest.fulfilled, (state, action) => {
        state.loading = false;
        state.addQuestionSuccess = true;
        // Mevcut test sorularına yeni soruyu ekle
        const payloadData = extractPayloadData(action.payload);
        const createdQuestion = payloadData?.question || payloadData;
        if (createdQuestion?._id) {
          const exists = state.currentTestQuestions.some(
            (q) => q._id === createdQuestion._id,
          );
          if (!exists) {
            state.currentTestQuestions.push(createdQuestion);
          }
          // Soru numarasına göre sırala
          state.currentTestQuestions.sort(
            (a, b) => (a.questionNumber || 0) - (b.questionNumber || 0),
          );
        }
      })
      .addCase(addQuestionToTest.rejected, (state, action) => {
        state.loading = false;
        state.addQuestionSuccess = false;
        state.error = action.payload;
      })

      // Test Cevabı Gönderme
      .addCase(submitTestResponse.pending, (state) => {
        state.loading = true;
        state.submitResponseSuccess = false;
        state.error = null;
      })
      .addCase(submitTestResponse.fulfilled, (state, action) => {
        state.loading = false;
        state.submitResponseSuccess = true;
        // Yeni test sonucunu kullanıcı sonuçlarına ekle
        if (action.payload.data) {
          state.userTestResults.unshift(action.payload.data);
        }
      })
      .addCase(submitTestResponse.rejected, (state, action) => {
        state.loading = false;
        state.submitResponseSuccess = false;
        state.error = action.payload;
      })

      // Kullanıcı Test Sonuçlarını Getirme
      .addCase(getUserTestResults.pending, (state) => {
        state.testResultsLoading = true;
        state.error = null;
      })
      .addCase(getUserTestResults.fulfilled, (state, action) => {
        state.testResultsLoading = false;
        state.userTestResults = action.payload.data || [];
      })
      .addCase(getUserTestResults.rejected, (state, action) => {
        state.testResultsLoading = false;
        state.error = action.payload;
      })

      // Soru Silme
      .addCase(deleteQuestion.pending, (state) => {
        state.loading = true;
        state.deleteQuestionSuccess = false;
        state.error = null;
      })
      .addCase(deleteQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteQuestionSuccess = true;
        // Silinen soruyu listeden çıkar
        state.currentTestQuestions = state.currentTestQuestions.filter(
          (question) => question._id !== action.payload.questionId,
        );
      })
      .addCase(deleteQuestion.rejected, (state, action) => {
        state.loading = false;
        state.deleteQuestionSuccess = false;
        state.error = action.payload;
      })

      // Test Silme
      .addCase(deleteTest.pending, (state) => {
        state.loading = true;
        state.deleteTestSuccess = false;
        state.error = null;
      })
      .addCase(deleteTest.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteTestSuccess = true;
        // Silinen testi listeden çıkar
        state.tests = state.tests.filter(
          (test) => test._id !== action.payload.testId,
        );
      })
      .addCase(deleteTest.rejected, (state, action) => {
        state.loading = false;
        state.deleteTestSuccess = false;
        state.error = action.payload;
      })

      // Soru Güncelleme
      .addCase(updateQuestion.pending, (state) => {
        state.loading = true;
        state.updateQuestionSuccess = false;
        state.error = null;
      })
      .addCase(updateQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.updateQuestionSuccess = true;
        const payloadData = extractPayloadData(action.payload);
        const updatedQuestion = payloadData?.question || payloadData;
        if (!updatedQuestion?._id) {
          return;
        }
        // Güncellenmiş soruyu listede güncelle
        const questionIndex = state.currentTestQuestions.findIndex(
          (question) => question._id === updatedQuestion._id,
        );
        if (questionIndex !== -1) {
          state.currentTestQuestions[questionIndex] = updatedQuestion;
        }
      })
      .addCase(updateQuestion.rejected, (state, action) => {
        state.loading = false;
        state.updateQuestionSuccess = false;
        state.error = action.payload;
      })

      // Test Güncelleme
      .addCase(updateTest.pending, (state) => {
        state.loading = true;
        state.updateTestSuccess = false;
        state.error = null;
      })
      .addCase(updateTest.fulfilled, (state, action) => {
        state.loading = false;
        state.updateTestSuccess = true;
        const updatedTest = extractPayloadData(action.payload);
        state.currentTest = updatedTest || state.currentTest;
        if (!updatedTest?._id) {
          return;
        }
        // Tests listesindeki testi de güncelle
        const testIndex = state.tests.findIndex(
          (test) => test._id === updatedTest._id,
        );
        if (testIndex !== -1) {
          state.tests[testIndex] = updatedTest;
        }
      })
      .addCase(updateTest.rejected, (state, action) => {
        state.loading = false;
        state.updateTestSuccess = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearCurrentTest,
  resetTestStates,
  setCreateTestSuccess,
  setAddQuestionSuccess,
  setSubmitResponseSuccess,
  clearUserTestResults,
} = testSlice.actions;

export default testSlice.reducer;
